import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Lock, Mail, Heart, CheckCircle2, X } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password, rememberMe);
      // Honour `?next=` set by the 401 interceptor, falling back to the
      // dashboard. Refuse absolute or cross-origin `next` values — only
      // safe in-app paths starting with `/` (not `//`).
      const next = searchParams.get("next");
      const safeNext =
        next && next.startsWith("/") && !next.startsWith("//") ? next : "/";
      nav(safeNext, { replace: true });
    } catch (e) {
      // Generic error — never leak whether email exists or password is wrong
      setErr("Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left: brand panel */}
      <div className="hidden md:flex relative flex-col justify-between p-12 text-primary-foreground overflow-hidden"
           style={{ background: "linear-gradient(160deg, #5B3AA4 0%, #4A2E88 55%, #3A246B 100%)" }}>
        <div className="absolute inset-0 opacity-40"
             style={{
               backgroundImage:
                 "radial-gradient(circle at 25% 15%, rgba(110,184,59,0.35), transparent 45%), radial-gradient(circle at 85% 85%, rgba(255,255,255,0.12), transparent 45%)",
             }} />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center p-1 shadow-lg">
              <img src="/abelia-logo.jpg" alt="Abelia" className="w-full h-full object-contain rounded-xl" />
            </div>
            <div>
              <div className="font-semibold tracking-tight text-xl" style={{fontFamily:'Outfit'}}>Abelia</div>
              <div className="text-[10px] uppercase tracking-[0.22em] opacity-70">Empowering · Inspiring · Independent</div>
            </div>
          </div>
        </div>
        <div className="relative">
          <h1 className="text-5xl font-semibold leading-[1.05] tracking-tight mb-6" style={{fontFamily:'Outfit'}}>
            Empowering<br/>different abilities,<br/><span style={{color:'#A6E075'}}>inspiring</span> independence.
          </h1>
          <p className="text-primary-foreground/80 max-w-md leading-relaxed">
            The compliance-first workforce portal for direct support professionals, nurses, and administrators.
            Real-time credential validation, GPS-aware clock-ins, audit-ready from day one.
          </p>
          <ul className="mt-8 space-y-2.5 text-primary-foreground/85">
            {[
              "Hard compliance locks on scheduling & clock-in",
              "200 ft GPS geofence enforcement",
              "30-day credential expiry alerts",
              "Exportable audit trail for state inspectors",
            ].map((t) => (
              <li key={t} className="flex items-center gap-2.5 text-[14px]">
                <CheckCircle2 size={15} style={{color:'#A6E075'}} /> {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative flex items-center gap-2 text-xs opacity-60">
          <Heart size={12} /> Built for regulated residential care.
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-background">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form" autoComplete="on">
          <div className="md:hidden mb-8 flex items-center gap-3">
            <img src="/abelia-logo.jpg" alt="Abelia" className="w-11 h-11 rounded-xl object-contain bg-white ring-1 ring-border p-0.5"/>
            <div>
              <div className="font-semibold text-primary" style={{fontFamily:'Outfit'}}>Abelia ACCP</div>
              <div className="accp-label text-[9px]">Compliance Portal</div>
            </div>
          </div>
          <div className="accp-label mb-3">Sign in</div>
          <h2 className="text-3xl font-semibold text-primary mb-2" style={{fontFamily:'Outfit'}}>Welcome back.</h2>
          <p className="text-muted-foreground mb-8">Enter your credentials to access the portal.</p>

          <label className="block mb-4">
            <span className="accp-label">Email</span>
            <div className="relative mt-1.5">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
              <input
                type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                className="accp-input pl-10" data-testid="login-email" autoComplete="email"
                inputMode="email" autoCapitalize="none" spellCheck={false}
              />
            </div>
          </label>

          <label className="block mb-2">
            <span className="accp-label">Password</span>
            <div className="relative mt-1.5">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
              <input
                type="password" required value={password} onChange={(e)=>setPassword(e.target.value)}
                className="accp-input pl-10" data-testid="login-password" autoComplete="current-password"
              />
            </div>
          </label>

          <div className="flex items-center justify-between mb-6">
            <label className="inline-flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none"
                   data-testid="remember-me-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-border accent-primary"
                data-testid="remember-me"
              />
              Remember me <span className="text-muted-foreground/70">(30 days)</span>
            </label>
            <button type="button" onClick={()=>setForgotOpen(true)}
                    className="text-xs font-semibold text-primary hover:underline"
                    data-testid="forgot-password-link">
              Forgot password?
            </button>
          </div>

          {err && (
            <div className="mb-4 rounded-xl px-4 py-3 compliance-expired text-sm" data-testid="login-error">
              {err}
            </div>
          )}

          <button type="submit" className="accp-btn-primary w-full py-3" disabled={loading} data-testid="login-submit">
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>

      {forgotOpen && (
        <div className="accp-modal-shell" onClick={()=>setForgotOpen(false)}>
          <div className="accp-modal-card space-y-4" onClick={(e)=>e.stopPropagation()} data-testid="forgot-password-modal">
            <div className="flex items-start justify-between">
              <h3 className="text-xl font-semibold text-primary" style={{fontFamily:'Outfit'}}>Reset password</h3>
              <button type="button" onClick={()=>setForgotOpen(false)} aria-label="Close"><X size={18}/></button>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Please contact your administrator to reset your password.
            </p>
            <a href="mailto:admin@abeliainc.com"
               className="accp-btn-primary w-full py-3"
               data-testid="forgot-password-mailto">
              admin@abeliainc.com
            </a>
            <p className="text-xs text-muted-foreground">
              For your security, passwords can only be reset by a verified administrator.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
